import React from 'react';
import { Button } from './ui/Button';
import { FileText, Clock, Calendar, CheckCircle2, Printer, Star, Users, BookOpen, Gift, Shield, Lock, CreditCard } from 'lucide-react';
export function Hero() {
  return <section className="relative overflow-hidden pt-8 pb-12 px-4 md:pt-16 md:pb-20" data-id="element-201">
      {/* subtle radial glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 blur-[120px] rounded-full pointer-events-none" data-id="element-202" />

      <div className="relative max-w-5xl mx-auto text-center" data-id="element-203">
        {/* Eyebrow */}
        <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-[11px] md:text-sm font-bold text-primary mb-5 md:mb-7 font-space-grotesk tracking-wider uppercase" data-id="element-204">
          🎒 COMPRA DIGITAL YA™
        </div>

        {/* Headline */}
        <h1 className="text-[1.65rem] md:text-6xl font-extrabold leading-[1.1] mb-4 md:mb-6 tracking-tight text-slate-900" data-id="element-205">
          Todo tu año de Primer Grado,{' '}
          <span className="text-primary" data-id="element-206">listo desde hoy.</span>
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-2xl text-slate-600 mb-2 md:mb-4 max-w-3xl mx-auto leading-snug font-medium" data-id="element-207">
          Sin buscar. Sin armar. Sin perder el fin de semana.
        </p>

        {/* Mockup */}
        <a href="#pricing" className="my-5 md:my-8 flex justify-center group" aria-label="Ver oferta del Combo Completo Primer Grado" data-id="element-208">
          <img src="https://cdn.magicpatterns.com/uploads/9vGcsxBa8yujZKfaBwozM6/ChatGPT_Image_6_jul_2026,_06_50_36_p.m..png" alt="Combo Completo Primer Grado — 6 Tomos + 6 Bonos" className="w-[65%] max-w-[460px] md:w-full md:max-w-[520px] aspect-square object-contain drop-shadow-[0_20px_40px_rgba(37,99,235,0.15)] transition-transform duration-300 group-hover:scale-[1.02]" data-id="element-209" />
        </a>

        {/* Support copy */}
        <p className="text-sm md:text-lg text-slate-900 font-bold mb-2 max-w-2xl mx-auto" data-id="element-210">
          Combo Completo Primer Grado 2026 — 6 Tomos + 6 Bonos
        </p>
        <p className="text-xs md:text-base text-slate-500 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed" data-id="element-211">
          El sistema N°1 para docentes de Primer Grado en Argentina. Todo el año
          resuelto, de Marzo a Diciembre.
        </p>

        {/* Quick bullets */}
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 md:gap-x-5 mb-7 md:mb-8 text-xs md:text-sm text-slate-600 font-medium" data-id="element-212">
          <div className="flex items-center gap-1.5" data-id="element-213">
            <FileText className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" data-id="element-214" /> 100%
            PDF
          </div>
          <div className="flex items-center gap-1.5" data-id="element-215">
            <Clock className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" data-id="element-216" /> Acceso
            en 5 min
          </div>
          <div className="flex items-center gap-1.5" data-id="element-217">
            <Calendar className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" data-id="element-218" />{' '}
            Primer Grado 2026
          </div>
          <div className="flex items-center gap-1.5" data-id="element-219">
            <Printer className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" data-id="element-220" /> Listo
            para imprimir
          </div>
        </div>

        {/* CTA */}
        <Button href="#pricing" className="text-base md:text-2xl py-4 md:py-5 px-8 md:px-10 w-full md:w-auto mb-4 md:mb-5" data-id="element-221">
          👉 QUIERO MI COMBO AHORA
        </Button>

        {/* Trust microcopy */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-[11px] md:text-sm text-slate-500 font-medium mb-10 md:mb-14" data-id="element-222">
          <span className="flex items-center gap-1" data-id="element-223">
            <Shield className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" data-id="element-224" />{' '}
            Garantía 7 días
          </span>
          <span className="text-slate-300" data-id="element-225">·</span>
          <span className="flex items-center gap-1" data-id="element-226">
            <Lock className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" data-id="element-227" /> Pago
            seguro
          </span>
          <span className="text-slate-300" data-id="element-228">·</span>
          <span className="flex items-center gap-1" data-id="element-229">
            <CreditCard className="w-3 h-3 md:w-3.5 md:h-3.5 text-primary" data-id="element-230" />{' '}
            Mercado Pago
          </span>
        </div>

        {/* Social proof badges */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto" data-id="element-231">
          {[{
          icon: Users,
          label: '+300 docentes confían'
        }, {
          icon: BookOpen,
          label: '6 Tomos listos para usar'
        }, {
          icon: Gift,
          label: '6 Bonos incluidos gratis'
        }, {
          icon: Star,
          label: '4.9/5 ⭐⭐⭐⭐⭐'
        }].map(({
          icon: Icon,
          label
        }, i) => <div key={i} className="bg-white border border-slate-200 shadow-sm rounded-2xl p-3 md:p-4 flex flex-col items-center gap-1.5 hover:border-primary/40 transition-colors" data-id="element-232">
              <Icon className="w-4 h-4 md:w-5 md:h-5 text-primary" data-id="element-233" />
              <span className="text-[11px] md:text-sm font-bold text-slate-800 text-center leading-tight" data-id="element-234">
                {label}
              </span>
            </div>)}
        </div>
      </div>
    </section>;
}